import React from 'react';
import { Grid } from '@material-ui/core/index';
import * as Yup from 'yup';

import * as api from 'src/utils/api';
import useGetRequest from 'src/hooks/useGetRequest';
import {
  Form as FormInterface, Field, Perspective
} from 'src/types';
import BaseModal from 'src/components/Modal/BaseModal';

import PerspectiveRenderer from '../PerspectiveRenderer';
import { FIELD_DETAILS_BY_FIELD_TYPE } from '../../../constants/fieldConstants';
import FormikForm from '../../../../components/formik/FormikForm';

const Content = ({
  onSuccess,
  formId,
  perspective,
  workflowId,
  engagementId,
}: {
  perspective: Perspective,
  workflowId: string,
  formId: string,
  engagementId?: string,
  onSuccess: () => void,
}) => {
  const { result: table, isLoading } = useGetRequest({
    url: `/automatic/workflows/${workflowId}/table/`,
    initialResult: { fields: [] },
  });

  if (isLoading) return null;

  const fieldByFieldId: Record<string, Field> = {};

  table.fields.forEach((field: Field) => {
    fieldByFieldId[field.id] = field;
  });

  const fieldsConfig = perspective.widgets.filter((_widget: any) => _widget.fieldId).map((_widget: any) => {
    const field = fieldByFieldId[_widget.fieldId];
    return ({
      name: field.id.toString(), // todo - no more toStrings after uuid migration
      validator: _widget.data.isRequired ? FIELD_DETAILS_BY_FIELD_TYPE[field.type].validator : null,
      defaultValue: _widget.data.defaultValue,
    });
  });

  const widgetsWithComponentProps = perspective.widgets.map((_widget: any) => {
    // todo - this is cloned for the editor
    if (_widget.fieldId) {
      const field = fieldByFieldId[_widget.fieldId];
      return {
        ..._widget,
        componentProps: {
          name: field.id,
          label: field.name,
          options: field.options,
          isRequired: _widget.data.isRequired
        }
      };
    }
    return {
      ..._widget,
      componentProps: {},
    };
  });

  const initialValues: Record<string, any> = {};
  fieldsConfig.forEach((field: any) => {
    initialValues[field.name] = field.defaultValue;
  });

  const submitForm = (values: any) => {
    api.submitForm(workflowId, formId, values).then(() => {
      if (onSuccess) onSuccess();
    });
  };

  const onSubmit = (values: Record<string, any>) => {
    submitForm(values);
  };

  const validationSchema: Record<string, any> = {};
  fieldsConfig.forEach((field: any) => {
    if (field.validator) validationSchema[field.name] = field.validator;
  });

  return (
    <FormikForm
      initialValues={initialValues}
      validationSchema={Yup.object().shape({ ...validationSchema })}
      onSubmit={onSubmit}
    >
      <Grid item xs={12}>
        <PerspectiveRenderer
            // dirty hack - widgets need field data in order to render properly as a formik field
          perspective={{
            ...perspective,
            widgets: widgetsWithComponentProps
          }}
          workflowId={workflowId}
          widgetProps={{
            workflowId
          }}
          engagementId={engagementId}
        />
      </Grid>
    </FormikForm>
  );
};

interface Props {
  formId: string,
  workflowId: string,
  engagementId?: string,
  closeModal: () => void,
  onSuccess: () => void,
}

const FormFromFormIdRenderer = ({
  formId,
  workflowId,
  engagementId,
  closeModal,
  onSuccess,
}: Props) => {
  const {
    result: form,
    isLoading,
  }: { result: FormInterface, isLoading: boolean } = useGetRequest({
    url: `/automatic/workflows/${workflowId}/forms/${formId}/`,
  });

  if (isLoading) return null;

  return (
    <BaseModal
      onClose={closeModal}
      title={form.perspective.name}
      maxWidth={form.perspective.width}
      isOpen
      dialogProps={{
        disablePortal: true,
      }}
    >
      <Content
        formId={formId}
        onSuccess={onSuccess}
        perspective={form.perspective}
        workflowId={workflowId}
        engagementId={engagementId}
      />
    </BaseModal>
  );
};

export default FormFromFormIdRenderer;
